// src/encounter/services/use-save-family-history.ts
// Rail-scoped save hook for the IPS Family History section. Wraps the curl-verified
// FamilyMedicalHistory endpoints (POST /api/Patients/CreateFamilyMedicalHistory,
// PUT /api/Patients/familyMedicalHistory/{id}) and invalidates BOTH the encounter summary query
// (['patient-summary', patientId] — the rail render source) AND the chart-detail list
// (['patient-family-history', patientId] — the full-record source edit/delete read from). Mirrors
// use-save-condition / use-save-allergy: transport + cache only; body assembly + wire-shape rules
// live in lib/to-family-history-request.ts.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/shared/api/api-client';
import type { FamilyHistoryRequest } from '@/encounter/lib/to-family-history-request';

export function useSaveFamilyHistory(patientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<string, Error, FamilyHistoryRequest>({
    mutationFn: (req: FamilyHistoryRequest) => {
      if (req.kind === 'create') {
        return post<string>('/api/Patients/CreateFamilyMedicalHistory', req.body);
      }
      // update path covers EDIT and soft-DELETE.
      return put<string>(`/api/Patients/familyMedicalHistory/${req.familyHistoryId}`, req.body);
    },
    onSuccess: () => {
      // Invalidate the summary (rail render source) AND the full chart list the rail sources
      // edit/delete from (usePatientFamilyHistory, ['patient-family-history', patientId]). Without
      // this a freshly-added/edited row's full record stays stale and its affordance can't complete
      // the round-trip.
      qc.invalidateQueries({ queryKey: ['patient-summary', patientId] });
      qc.invalidateQueries({ queryKey: ['patient-family-history', patientId] });
    },
  });
}
